import { type CartProduct, type FetchResponse, formatStoreProductData, normalizeStoreAlias } from "shared-components";

import { actionHandler } from "../action-handler";
import { clientDelete, clientGet, clientPatch, clientPost } from "../client";
import type {
  ApiCheckoutResponse,
  ApiShoppingCart,
  ApiShoppingCartItem,
  CreateCartItemDto,
  CheckoutPayload,
  CheckoutResponse,
  PreCheckoutPayload,
  PreCheckoutResponse,
  ShoppingCart,
  ShoppingCartItem,
  StockCheckQuery,
  StockCheckResponse,
  UpdateCartItemDto,
} from "./models";

const formatShoppingCartItemsFromApi = (itemsFromApi: ApiShoppingCartItem[]): ShoppingCartItem[] =>
  itemsFromApi.map((item) => ({
    ...item,
    cartProduct: formatStoreProductData(item.cartProduct) as CartProduct,
  }));

const resolveStoreAliasPath = (path: string, storeAlias?: string): string => {
  const normalizedStoreAlias = normalizeStoreAlias(storeAlias);

  if (!normalizedStoreAlias) {
    return path;
  }

  const searchParams = new URLSearchParams({ storeAlias: normalizedStoreAlias });

  return `${path}?${searchParams.toString()}`;
};

export const addProductToShoppingCart = actionHandler(
  async (payload: CreateCartItemDto): Promise<FetchResponse<ShoppingCart>> => {
    return clientPost({ path: "cart/item", payload });
  },
);

export const loadClientShoppingCart = async (
  storeAlias?: string,
): Promise<ShoppingCart & { error?: { message: string } }> => {
  const response: ApiShoppingCart = await clientGet({ path: resolveStoreAliasPath("cart/items", storeAlias) });

  // Formata dados dos produtos se disponíveis
  if (response.items) {
    return {
      ...response,
      items: formatShoppingCartItemsFromApi(response.items),
    };
  }

  return response as ShoppingCart;
};

export const migrateCustomProductImages = async (): Promise<{ error?: { message: string } }> => {
  return clientPost({ path: "cart/custom-product-images-migration" });
};

export const deleteShoppingCartItem = actionHandler(
  async ({
    itemId,
    storeAlias,
  }: { itemId: string; storeAlias?: string }): Promise<ShoppingCart & { error?: { message: string } }> => {
    return clientDelete({ path: resolveStoreAliasPath(`cart/item/${itemId}`, storeAlias) });
  },
);

export const updateShoppingCartItem = actionHandler(
  async ({ id, ...payload }: UpdateCartItemDto): Promise<ShoppingCart & { error?: { message: string } }> => {
    return clientPatch({ path: `cart/item/${id}`, payload });
  },
);

/**
 * Valida se há estoque suficiente para um SKU antes do upload de imagens.
 * Usado como pre-check no fluxo de customização para evitar uploads desnecessários.
 */
export const checkBlankSkuStock = async (
  query: StockCheckQuery,
): Promise<StockCheckResponse & { error?: { message: string } }> => {
  const params = new URLSearchParams({
    blankId: query.blankId,
    colorName: query.colorName,
    colorHex: query.colorHex,
    size: query.size,
    amount: String(query.amount),
  });
  return clientGet({ path: `cart/stock-check?${params.toString()}` });
};

// Checkout APIs
export const preCheckoutCart = async (
  payload: PreCheckoutPayload,
): Promise<PreCheckoutResponse & { error?: { message: string } }> => {
  return clientPost({ path: "cart/pre-checkout", payload });
};

export const checkoutCart = actionHandler(
  async (payload: CheckoutPayload): Promise<CheckoutResponse & { error?: { message: string } }> => {
    const response: ApiCheckoutResponse = await clientPost({ path: "cart/checkout", payload });

    // Formata dados dos produtos se disponíveis
    if (response.items) {
      return {
        ...response,
        items: formatShoppingCartItemsFromApi(response.items),
      };
    }

    return response as CheckoutResponse;
  },
);
